// Import API configuration
import { API_CONFIG } from '../config/api.config';

// Import the default hotel image
import DefaultHotelImage from '../assets/TF-sun-icon_default.png';

// Default fallback image for hotels (TF sun icon with 50% opacity)
const DEFAULT_HOTEL_IMAGE = DefaultHotelImage;

// Full URL for the default hotel image when sending to external APIs
const DEFAULT_HOTEL_IMAGE_FULL_URL = 'https://www.travelfindholidays.com/static/media/TF-sun-icon_default.89a0c797329df656cb1f.png';

// Function to expand region codes to individual airport codes using Redux store data
const expandAirportCodes = (departureAirportId, airports) => {
  // Find the region in the airports array (which comes from Redux store)
  const region = airports.find(airport => 
    airport.isRegion && airport.code === departureAirportId
  );
  
  // If it's a region, return the constituent airports
  if (region && region.childAirports) {
    return region.childAirports.map(airport => airport.code);
  }
  
  // If it's an individual airport code, return as array
  return [departureAirportId];
};

// Helper to format dates
const formatDate = (date) => {
  if (typeof date === 'string') return date;
  return date.toISOString().split('T')[0];
};

// Helper to add days to a date
const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

  // Map sorting options to GraphQL enum values (using PackageSort enum)
  const mapSortBy = (sortBy) => {
    const sortMapping = {
      'price-asc': 'PRICE_ASC',
      'price-desc': 'PRICE_DESC',
      'date-asc': 'DEPARTURE_DATE_ASC',
      'date-desc': 'DEPARTURE_DATE_DESC',
      'stars-desc': 'STAR_RATING_DESC',
      'stars-asc': 'STAR_RATING_ASC',
      'default': 'PRICE_ASC'
    };
    return sortMapping[sortBy] || 'PRICE_ASC';
  };

// Store for pagination tokens
let paginationTokens = {};

export const fetchHotels = async (page = 1, limit = 10, sortBy = 'default', searchParams, airports = []) => {
  const { date, destination, departureAirport, duration, boardBasis, starRating } = searchParams || {};

  // Validate required search parameters
  if (!departureAirport || !destination || !date) {
    console.log('Missing required parameters:', {
      hasDepartureAirport: !!departureAirport,
      hasDestination: !!destination,
      hasDate: !!date
    });
    return {
      hotels: [],
      pagination: {
        currentPage: page,
        totalPages: 0,
        totalResults: 0
      }
    };
  }

  // Handle departure airports (use first one if array is provided)
  const departureAirportId = Array.isArray(departureAirport) 
    ? departureAirport[0].code 
    : departureAirport?.code || departureAirport;

  // Expand region codes to individual airport codes using Redux store data
  const departureAirportCodes = expandAirportCodes(departureAirportId, airports);

  // Destructure the destination tuple if it exists
  const [destinationCountry, destinationRegion, destinationResort] = 
    destination?.destinationTuple || destination?.split(',') || ['', '', ''];

  // Calculate date range based on duration
  const startDate = formatDate(date);
  const endDate = formatDate(addDays(date, duration || 7));

  // Build GraphQL query - using the updated schema with separate parameters
  const query = `
    query SearchPackages($filters: PackageSearchFilters!, $sortBy: PackageSort, $limit: Int, $nextToken: String) {
      searchPackages(filters: $filters, sortBy: $sortBy, limit: $limit, nextToken: $nextToken) {
        nextToken
        items {
          ItemKey
          TotalPrice
          HolidayCountry
          HolidayRegion
          HolidayResort
          Hotel
          FlightNumber_Outbound
          FlightNumber_Inbound
          DepartureDate
          Duration
          AdditionalData
          BoardBasis
          Brand
          DepartureAirportCode_Inbound
          DepartureAirportCode_Outbound
          Flight
          FreeChild
          LuggageIncluded
          HotelIdentifierCode
          NumberOfAdults
          NumberOfChildren
          NumberOfInfants
          Occupancy
          RoomCount
          RoomTypeDescription
          claivaUUID
          _EventId
          _Expiry
          _MessageId
          _ProcessedAt
          _Source
          _SubmittedAt
        }
        count
        errors
        hasMoreData
        queriesExecuted
        totalReturned
      }
    }
  `;

  // Calculate offset for pagination
  const offset = (page - 1) * limit;
  
  // Get the appropriate nextToken for pagination
  const nextToken = page > 1 ? paginationTokens[`page_${page - 1}`] : null;
  
  // Build filter object for GraphQL - using PackageSearchFilters schema (without pagination/sort parameters)
  const filters = {
    brand: API_CONFIG.BRAND_CODE,
    startDate,
    endDate,
    departureAirports: departureAirportCodes, // Expanded airport codes (individual airports or region constituents)
    numberOfAdults: API_CONFIG.DEFAULT_ADULTS,
    numberOfChildren: API_CONFIG.DEFAULT_CHILDREN,
    numberOfInfants: API_CONFIG.DEFAULT_INFANTS,
    duration: duration || API_CONFIG.DEFAULT_DURATION
  };

  // Add destination filters
  if (destinationCountry) filters.country = destinationCountry;
  if (destinationRegion) filters.region = destinationRegion;
  if (destinationResort) filters.resort = destinationResort;
  
  // Add optional filters
  if (boardBasis) filters.boardBasis = boardBasis;
  if (starRating) filters.starRating = parseInt(starRating);

  try {
                   // Debug logging in development
     if (API_CONFIG.DEBUG_MODE) {
       console.log('GraphQL Request:', {
         url: API_CONFIG.GRAPHQL_API_URL,
         originalAirport: departureAirportId,
         expandedAirports: departureAirportCodes,
         isRegionExpansion: departureAirportCodes.length > 1,
         filters,
         page
       });
     }

    const response = await fetch(API_CONFIG.GRAPHQL_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_CONFIG.API_KEY, // Use x-api-key header like priceApiService
      },
      body: JSON.stringify({
        query,
        variables: {
          filters,
          sortBy: mapSortBy(sortBy),
          limit: limit,
          nextToken: nextToken
        }
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error response:', errorText);
      throw new Error(`API Error: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    
    if (data.errors) {
      console.error('GraphQL errors:', data.errors);
      throw new Error('GraphQL query failed');
    }

    const result = data.data?.searchPackages;
    
    // Debug logging in development
    if (API_CONFIG.DEBUG_MODE) {
      console.log('GraphQL Response:', {
        count: result?.count,
        hasMoreData: result?.hasMoreData,
        itemsCount: result?.items?.length,
        nextToken: result?.nextToken
      });
    }
    
    // Handle empty or invalid results
    if (!result || !result.items || result.items.length === 0) {
      return {
        hotels: [],
        pagination: {
          currentPage: page,
          totalPages: 0,
          totalResults: 0,
          hasMoreData: false
        }
      };
    }
    
    // Store the nextToken for pagination (if available)
    if (result.nextToken) {
      paginationTokens[`page_${page}`] = result.nextToken;
    }

    // Transform GraphQL response to match existing format
    const hotels = result.items.map(item => {
      // Parse JSON strings from the response
      const hotelData = item.Hotel ? JSON.parse(item.Hotel) : {};
      const flightData = item.Flight ? JSON.parse(item.Flight) : {};
      const additionalData = item.AdditionalData ? JSON.parse(item.AdditionalData) : {};

      // Calculate price per adult (assuming 2 adults by default)
      const pricePerAdult = item.TotalPrice / (item.NumberOfAdults || 2);

      return {
        ItemKey: item.ItemKey,
        CacheKey: item.ItemKey, // Use ItemKey as CacheKey
        Brand: item.Brand,
        LastUpdate: new Date().toISOString(),
        Expiry: item._Expiry,
        HolidayCountry: item.HolidayCountry,
        HolidayRegion: item.HolidayRegion,
        HolidayResort: item.HolidayResort,
        Duration: item.Duration,
        BoardBasis: item.BoardBasis,
        RoomTypeDescription: item.RoomTypeDescription,
        DeeplinkUrl: additionalData.DeeplinkUrl || '',
        AdditionalAttributes: {
          Information: [] // Not available in new API, leaving empty for compatibility
        },
        PriceDetails: {
          Currency: 'GBP', // Assuming GBP, update as needed
          PricePerAdult: pricePerAdult,
          PricePerChild: 0, // Not available in new API
          PricePerInfant: 0, // Not available in new API
          NumberOfAdults: item.NumberOfAdults,
          NumberOfChildren: item.NumberOfChildren,
          NumberOfInfants: item.NumberOfInfants,
          TotalPriceBeforeDiscount: parseFloat(additionalData.TotalPriceBeforeDiscount || item.TotalPrice),
          TotalPrice: item.TotalPrice,
          AdjustedPrice: item.TotalPrice,
          Extras: {
            TransfersIncluded: false, // Not available in new API
            LuggageIncluded: item.LuggageIncluded > 0,
            Bonded: false // Not available in new API
          }
        },
        HotelDetails: {
           HotelIdentifierCode: item.HotelIdentifierCode,
           HotelName: hotelData.HotelName || '',
           StarRating: parseFloat(hotelData.StarRating || 0),
           ConsumerRating: parseFloat(hotelData.ConsumerRating || 0),
           HotelURL: '', // Not available in new API
           Longitude: parseFloat(hotelData.Longitude || 0),
           Latitude: parseFloat(hotelData.Latitude || 0),
           HotelImages: hotelData.ImageURL ? [hotelData.ImageURL] : [DEFAULT_HOTEL_IMAGE_FULL_URL]
         },
        FlightDetails: {
          Outbound: {
            DepartureDateTime: flightData.DepartureDateTime_Outbound || '',
            DepartureAirportCode: item.DepartureAirportCode_Outbound,
            ArrivalDateTime: flightData.ArrivalDateTime_Outbound || '',
            ArrivalAirportCode: flightData.ArrivalAirportCode_Outbound || '',
            FlightNumber: item.FlightNumber_Outbound,
            AirlineName: flightData.AirlineName_Outbound || '',
            NumberOfStops: 0, // Not available in new API
            FlightDuration: Math.floor((parseInt(flightData.FlightDuration_Outbound || 0)) / 60) // Convert seconds to minutes
          },
          Inbound: {
            DepartureDateTime: flightData.DepartureDateTime_Inbound || '',
            DepartureAirportCode: item.DepartureAirportCode_Inbound,
            ArrivalDateTime: flightData.ArrivalDateTime_Inbound || '',
            ArrivalAirportCode: flightData.ArrivalAirportCode_Inbound || '',
            FlightNumber: item.FlightNumber_Inbound,
            AirlineName: flightData.AirlineName_Inbound || '',
            NumberOfStops: 0, // Not available in new API
            FlightDuration: Math.floor((parseInt(flightData.FlightDuration_Inbound || 0)) / 60) // Convert seconds to minutes
          }
        }
      };
    });

    // Calculate pagination information
    // The API provides hasMoreData flag and nextToken for proper pagination
    let totalPages = page;
    const totalResults = result.count || hotels.length;
    
    // If we have more data, there's at least one more page
    if (result.hasMoreData || result.nextToken) {
      totalPages = page + 1;
    }
    
    // If we don't have more data and we're on page 1, then this is the only page
    if (!result.hasMoreData && !result.nextToken && page === 1) {
      totalPages = 1;
    }

    return {
      hotels,
      pagination: {
        currentPage: page,
        totalPages: totalPages,
        totalResults: totalResults,
        hasMoreData: result.hasMoreData || false
      }
    };

  } catch (error) {
    console.error('Search API Error:', error);
    throw error;
  }
};

// Helper function to get hotel images - to be implemented with lookup table
export const getHotelImages = async (hotelId) => {
  // TODO: Implement hotel image lookup
  // This would query a separate service or use a local lookup table
  return [];
}; 